/**
 * Cavitation Early Warning Dashboard
 *
 * Shows per-well cavitation risk timeline across 6 horizons (3mo→20yr).
 * Highlights which wells will cavitate first and when.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, ReferenceLine,
} from 'recharts';
import { ArrowPathIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/client';

interface CavitationResult {
  well_id: string;
  pump_setting_ft: number;
  current_dtw_ft: number;
  current_sat_ft: number;
  cavitation_horizon: string | null;
  timeline: {
    horizon: string;
    days: number;
    predicted_dtw_ft: number;
    predicted_sat_ft: number;
    remaining_above_pump_ft: number;
    cavitation_risk: number;
  }[];
}

const RISK_COLORS: Record<string, string> = {
  safe: '#10b981',
  low: '#22d3ee',
  moderate: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
};

function riskColor(risk: number): string {
  if (risk >= 0.8) return RISK_COLORS.critical;
  if (risk >= 0.5) return RISK_COLORS.high;
  if (risk >= 0.2) return RISK_COLORS.moderate;
  if (risk >= 0.05) return RISK_COLORS.low;
  return RISK_COLORS.safe;
}

interface CavitationPanelProps {
  siteId: string;
  wells: { well_id: string; well_name: string; latitude: number; longitude: number;
    total_depth_ft: number | null; ground_elevation_ft: number | null;
    depth_to_water_ft: number | null; water_elevation_ft: number | null }[];
  timeseries: any[];
}

export function CavitationPanel({ siteId, wells, timeseries }: CavitationPanelProps) {
  const [results, setResults] = useState<CavitationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWell, setSelectedWell] = useState<string | null>(null);

  const runCavitation = useCallback(async () => {
    setLoading(true);
    setError(null);
    const cavResults: CavitationResult[] = [];

    // Merge reference wells with imported wells (PumpAssets)
    let allWells = [...wells];
    try {
      const assetsResp = await apiClient.get(`/phreatic/pump-control/assets/${siteId}`);
      const importedAssets = assetsResp.data?.assets || [];
      for (const a of importedAssets) {
        // Skip if already in reference wells by name
        if (allWells.some(w => w.well_name === a.well_name)) continue;
        allWells.push({
          well_id: a.well_name,
          well_name: a.well_name,
          latitude: a.latitude || 0,
          longitude: a.longitude || 0,
          total_depth_ft: a.well_depth_ft || null,
          ground_elevation_ft: a.ground_elevation_ft || null,
          depth_to_water_ft: a.current_dtw_ft || null,
          water_elevation_ft: a.ground_elevation_ft && a.current_dtw_ft
            ? a.ground_elevation_ft - a.current_dtw_ft : null,
        });
      }
    } catch {
      // No imported wells, proceed with reference only
    }

    for (const w of allWells) {
      if (!w.total_depth_ft || !w.ground_elevation_ft || !w.depth_to_water_ft) continue;

      // Check timeseries from bundle (reference wells) first
      const ts = timeseries.find(t => t.well_name === w.well_name || t.well_id === w.well_id);

      // For imported wells without bundle timeseries, fetch gaugings from backend
      let measurements: any[] = [];
      if (ts && ts.measurements.length >= 3) {
        measurements = ts.measurements
          .filter((m: any) => m.water_elevation_ft != null && m.date)
          .slice(-20)
          .map((m: any, i: number, arr: any[]) => ({
            date: m.date,
            water_elevation_ft: m.water_elevation_ft,
            depth_to_water_ft: m.depth_to_water_ft ?? 0,
            precip_mm: 0, temp_c: 20, et0_mm: 0,
            days_since_last: i > 0 ? Math.max(1, Math.round((new Date(m.date).getTime() - new Date(arr[i-1].date).getTime()) / 86400000)) : 90,
          }));
      } else {
        // Try to get gaugings for imported wells
        try {
          const gResp = await apiClient.get(`/phreatic/pump-control/assets/${siteId}/gaugings-by-name?well_name=${encodeURIComponent(w.well_name)}`);
          const gaugings = gResp.data?.gaugings || [];
          if (gaugings.length >= 3) {
            measurements = gaugings
              .filter((g: any) => g.gauge_date)
              .slice(-20)
              .map((g: any, i: number, arr: any[]) => ({
                date: g.gauge_date,
                water_elevation_ft: g.water_elevation_ft ?? (w.ground_elevation_ft! - (g.depth_to_water_ft || 0)),
                depth_to_water_ft: g.depth_to_water_ft ?? 0,
                precip_mm: 0, temp_c: 20, et0_mm: 0,
                days_since_last: i > 0 ? Math.max(1, Math.round((new Date(g.gauge_date).getTime() - new Date(arr[i-1].gauge_date).getTime()) / 86400000)) : 90,
              }));
          }
        } catch {
          // No gaugings available for this well
        }
      }

      if (measurements.length < 3) continue;

      try {
        const { data } = await apiClient.post('/phreatic/v4/cavitation', {
          site_id: siteId,
          well_id: w.well_name,
          well_depth_ft: w.total_depth_ft,
          ground_elevation_ft: w.ground_elevation_ft,
          current_dtw_ft: w.depth_to_water_ft,
          measurements,
          aquifer_name: 'other',
          lat: w.latitude,
          lon: w.longitude,
        });
        cavResults.push(data);
      } catch (e) {
        // Skip wells that fail
      }
    }

    if (cavResults.length === 0 && allWells.length > 0) {
      setError('No wells had sufficient data for cavitation analysis. Wells need total_depth, ground_elevation, and depth_to_water with at least 3 gauging events.');
    }
    setResults(cavResults);
    setSelectedWell(cavResults[0]?.well_id || null);
    setLoading(false);
  }, [siteId, wells, timeseries]);

  useEffect(() => { runCavitation(); }, []);

  const criticalWells = results.filter(r => r.cavitation_horizon != null);
  const safeWells = results.filter(r => r.cavitation_horizon == null);
  const selected = results.find(r => r.well_id === selectedWell);

  return (
    <div className="space-y-3">
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">Cavitation Early Warning</h3>
          <button onClick={runCavitation} disabled={loading}
            className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50">
            {loading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : null}
            {loading ? 'Analyzing...' : 'Re-analyze'}
          </button>
        </div>

        {error && <div className="text-cyan-400 text-xs mb-3">{error}</div>}

        {results.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-mono-800/50 rounded p-2 text-center">
                <div className="text-2xl font-bold text-mono-200">{results.length}</div>
                <div className="text-[10px] text-mono-500">Wells Analyzed</div>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-center">
                <div className="text-2xl font-bold text-red-400">{criticalWells.length}</div>
                <div className="text-[10px] text-red-400">At Risk</div>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded p-2 text-center">
                <div className="text-2xl font-bold text-cyan-400">{safeWells.length}</div>
                <div className="text-[10px] text-cyan-400">Safe (20yr)</div>
              </div>
            </div>

            {criticalWells.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-1 text-red-400 text-xs font-semibold">
                  <ExclamationTriangleIcon className="w-4 h-4" /> Wells at risk:
                </div>
                {criticalWells.map(w => (
                  <div key={w.well_id} className="text-xs text-red-300">
                    {w.well_id} — cavitates at <span className="font-bold">{w.cavitation_horizon}</span> (DTW: {w.current_dtw_ft}ft, pump: {w.pump_setting_ft}ft)
                  </div>
                ))}
              </div>
            )}
            {criticalWells.length === 0 && (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 flex items-center gap-2 text-cyan-400 text-xs">
                <CheckCircleIcon className="w-4 h-4" /> All wells safe within 20-year window
              </div>
            )}
          </>
        )}
      </div>

      {/* Risk table */}
      {results.length > 0 && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-2">Per-Well Risk — click for detail</h3>
          <table className="w-full text-xs">
            <thead><tr className="text-mono-400 border-b border-mono-800">
              <th className="text-left py-2 px-2">Well</th>
              <th className="text-right py-2 px-2">DTW</th>
              <th className="text-right py-2 px-2">Pump</th>
              <th className="text-right py-2 px-2">Sat</th>
              <th className="text-center py-2 px-2">3mo</th>
              <th className="text-center py-2 px-2">1yr</th>
              <th className="text-center py-2 px-2">5yr</th>
              <th className="text-center py-2 px-2">10yr</th>
              <th className="text-center py-2 px-2">20yr</th>
              <th className="text-center py-2 px-2">Status</th>
            </tr></thead>
            <tbody>{results.map(r => (
              <tr key={r.well_id} onClick={() => setSelectedWell(r.well_id)}
                className={`border-b border-mono-800/50 cursor-pointer transition-colors ${selectedWell === r.well_id ? 'bg-cyan-500/10' : 'hover:bg-mono-800/50'} text-mono-300`}>
                <td className="py-1.5 px-2 font-medium">{r.well_id}</td>
                <td className="text-right py-1.5 px-2">{r.current_dtw_ft}ft</td>
                <td className="text-right py-1.5 px-2 text-mono-500">{r.pump_setting_ft}ft</td>
                <td className="text-right py-1.5 px-2">{r.current_sat_ft}ft</td>
                {['3mo', '1yr', '5yr', '10yr', '20yr'].map(hz => {
                  const step = r.timeline.find(t => t.horizon === hz);
                  return (
                    <td key={hz} className="text-center py-1.5 px-2">
                      {step ? <span className="inline-block w-3 h-3 rounded-full" style={{ background: riskColor(step.cavitation_risk) }} /> : <span className="text-mono-600">—</span>}
                    </td>
                  );
                })}
                <td className="text-center py-1.5 px-2">
                  {r.cavitation_horizon ? <span className="text-red-400 font-bold text-[10px]">{r.cavitation_horizon}</span> : <span className="text-cyan-400 text-[10px]">SAFE</span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
          <div className="flex items-center gap-3 mt-2 text-[9px] text-mono-500">
            {Object.entries(RISK_COLORS).map(([level, color]) => (
              <span key={level} className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: color }} />{level}</span>
            ))}
          </div>
        </div>
      )}

      {/* Selected well chart */}
      {selected && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-1">{selected.well_id} — Headroom Above Pump</h3>
          <p className="text-[10px] text-mono-500 mb-3">Pump at {selected.pump_setting_ft}ft | Bars show distance above pump intake | Below zero = cavitation</p>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={selected.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="horizon" tick={{ fill: '#888', fontSize: 11 }} />
              <YAxis tick={{ fill: '#888', fontSize: 10 }} label={{ value: 'Headroom (ft)', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11 }}
                formatter={(val: number) => [`${val.toFixed(1)} ft`, 'Headroom']} />
              <ReferenceLine y={0} stroke="#ef4444" strokeWidth={2} />
              <Bar dataKey="remaining_above_pump_ft" name="Headroom">
                {selected.timeline.map((t, i) => <Cell key={i} fill={riskColor(t.cavitation_risk)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {loading && results.length === 0 && (
        <div className="flex items-center justify-center h-32">
          <ArrowPathIcon className="w-5 h-5 text-mono-400 animate-spin" />
          <span className="ml-2 text-mono-400 text-sm">Analyzing cavitation risk...</span>
        </div>
      )}
    </div>
  );
}
